import { useState } from "react";
import { Save, Loader2, Plus, X, FlaskConical, Info, Database } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { QualityChip } from "@/components/Chips";

const inp = "h-9 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";
const lbl = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink/50";

function SaveBar({ onSave, saving }) {
  return (
    <button onClick={onSave} disabled={saving} data-testid="config-save-button" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save changes
    </button>
  );
}

async function saveConfig(body, onSaved, setSaving) {
  setSaving(true);
  try { await api.put("/automation/config", body); toast.success("Settings saved"); onSaved(); }
  catch (e) { toast.error(apiError(e)); }
  finally { setSaving(false); }
}

/* ---------------- Quality Thresholds ---------------- */
export function ThresholdsEditor({ config, options, onSaved }) {
  const [t, setT] = useState(JSON.parse(JSON.stringify(config.thresholds || {})));
  const [u, setU] = useState(JSON.parse(JSON.stringify(config.unknown || {})));
  const [saving, setSaving] = useState(false);
  const setT2 = (q, k, v) => setT((s) => ({ ...s, [q]: { ...s[q], [k]: v === "" ? undefined : Number(v) } }));

  const rows = [["Ace", ["min"]], ["King", ["min", "max"]], ["Queen", ["min", "max"]], ["Joker", ["max"]]];
  return (
    <div className="max-w-2xl space-y-5" data-testid="thresholds-editor">
      <div className="rounded-2xl border border-sky/70 bg-white p-4">
        <h3 className="mb-1 font-sora text-sm font-semibold text-navy">Score → Quality bands</h3>
        <p className="mb-3 text-xs text-ink/50">A lead's total score maps to a quality tier using these min/max bounds.</p>
        <div className="space-y-2.5">
          {rows.map(([q, keys]) => (
            <div key={q} className="flex items-center gap-3">
              <span className="w-16"><QualityChip value={q} /></span>
              {keys.map((k) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="text-xs text-ink/50">{k}</span>
                  <input type="number" data-testid={`threshold-${q}-${k}`} className="h-9 w-24 rounded-lg border border-sky px-2 text-sm" value={t[q]?.[k] ?? ""} onChange={(e) => setT2(q, k, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-sky/70 bg-white p-4">
        <h3 className="mb-1 font-sora text-sm font-semibold text-navy">Unknown / Not-Sure handling</h3>
        <p className="mb-3 text-xs text-ink/50">When too many answers are vague, flag for review and cap the quality.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Threshold % unknown</label><input type="number" className={inp} value={u.threshold_pct ?? ""} onChange={(e) => setU((s) => ({ ...s, threshold_pct: Number(e.target.value) }))} /></div>
          <div><label className={lbl}>Cap quality at</label>
            <select className={inp} value={u.max_quality || ""} onChange={(e) => setU((s) => ({ ...s, max_quality: e.target.value }))}>
              {(options?.qualities || []).map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Result status</label><input className={inp} value={u.result_status || ""} onChange={(e) => setU((s) => ({ ...s, result_status: e.target.value }))} /></div>
          <div className="flex items-end pb-1.5"><label className="flex items-center gap-2 text-sm text-ink/70"><input type="checkbox" checked={!!u.active} onChange={(e) => setU((s) => ({ ...s, active: e.target.checked }))} />Active</label></div>
          <div className="col-span-2"><label className={lbl}>Unknown labels (comma-separated)</label><input className={inp} value={(u.labels || []).join(", ")} onChange={(e) => setU((s) => ({ ...s, labels: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))} /></div>
        </div>
      </div>
      <SaveBar saving={saving} onSave={() => saveConfig({ thresholds: t, unknown: u }, onSaved, setSaving)} />
    </div>
  );
}

/* ---------------- Stage & Activity Rules ---------------- */
export function StageRulesEditor({ config, options, onSaved }) {
  const [sc, setSc] = useState(JSON.parse(JSON.stringify(config.stage_config || {})));
  const [saving, setSaving] = useState(false);
  const stages = options?.stages || Object.keys(sc);
  const setStage = (s, patch) => setSc((cur) => ({ ...cur, [s]: { ...(cur[s] || {}), ...patch } }));
  const setAct = (s, patch) => setSc((cur) => ({ ...cur, [s]: { ...(cur[s] || {}), activity: { ...((cur[s] || {}).activity || {}), ...patch } } }));

  const TOGGLES = [
    ["requires_qualification", "Qualification"],
    ["requires_activity", "Activity"],
    ["requires_lost_reason", "Lost reason"],
    ["requires_junk_reason", "Junk reason"],
    ["requires_deal_details", "Deal details"],
  ];
  return (
    <div className="space-y-3" data-testid="stage-rules-editor">
      <p className="text-xs text-ink/50">For each stage, choose what must be completed before a lead can be moved into it.</p>
      <div className="space-y-3">
        {stages.map((s) => {
          const cfg = sc[s] || {};
          return (
            <div key={s} data-testid={`stage-row-${s}`} className="rounded-2xl border border-sky/70 bg-white p-4">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-sora text-sm font-semibold text-navy">{options?.stage_meta?.[s]?.name || s}</h3>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-ink/50">Open activities:</span>
                  <select className="h-8 rounded-lg border border-sky px-2 text-xs" value={cfg.open_activity_action || "keep"} onChange={(e) => setStage(s, { open_activity_action: e.target.value })}>
                    {["keep", "ask", "complete", "cancel"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {TOGGLES.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs text-ink/70"><input type="checkbox" data-testid={`stage-${s}-${k}`} checked={!!cfg[k]} onChange={(e) => setStage(s, { [k]: e.target.checked })} />{label}</label>
                ))}
              </div>
              {cfg.requires_activity && (
                <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-mist/40 p-2 md:grid-cols-3">
                  <div><label className={lbl}>Default type</label>
                    <select className={inp} value={cfg.activity?.type || "Follow-up"} onChange={(e) => setAct(s, { type: e.target.value })}>
                      {(options?.activity_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><label className={lbl}>Default title</label><input className={inp} value={cfg.activity?.title || ""} onChange={(e) => setAct(s, { title: e.target.value })} /></div>
                  <div><label className={lbl}>Priority</label>
                    <select className={inp} value={cfg.activity?.priority || "Medium"} onChange={(e) => setAct(s, { priority: e.target.value })}>
                      {(options?.activity_priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <SaveBar saving={saving} onSave={() => saveConfig({ stage_config: sc }, onSaved, setSaving)} />
    </div>
  );
}

/* ---------------- Lost & Junk Rules ---------------- */
export function LostJunkEditor({ config, options, onSaved }) {
  const [lq, setLq] = useState(JSON.parse(JSON.stringify(config.lost_quality || {})));
  const [saving, setSaving] = useState(false);
  const TYPES = ["keep", "set", "cap", "downgrade_if_high", "no_response", "note_only"];
  // Lost reasons are now managed in Settings → Master Data; rows are generated from
  // that list so adding/removing a reason there flows straight into the mapping.
  const lostReasons = Array.isArray(config.lost_reasons)
    ? config.lost_reasons : Object.keys(lq);

  const save = () => {
    const next = {};
    lostReasons.forEach((r) => { next[r] = lq[r] || { type: "keep" }; });
    saveConfig({ lost_quality: next }, onSaved, setSaving);
  };

  return (
    <div className="space-y-4" data-testid="lostjunk-editor">
      <div className="flex items-start gap-2 rounded-xl border border-sky/60 bg-sky/10 p-3 text-xs text-ink/70" data-testid="junk-moved-note">
        <Database className="mt-0.5 h-4 w-4 shrink-0 text-loop" />
        <span><b className="text-navy">Junk reasons</b> and <b className="text-navy">Lost reasons</b> are now managed in <b>Settings → Master Data</b>. Add or rename them there; this page just maps each <b>Lost</b> reason to a quality behaviour.</span>
      </div>

      <div className="rounded-2xl border border-sky/70 bg-white p-4">
        <h3 className="mb-1 font-sora text-sm font-semibold text-navy">Lost reason → quality behaviour</h3>
        <p className="mb-3 text-xs text-ink/50">How quality should change when a lead is lost for each reason. Reasons come from Settings → Master Data → Lost Reasons.</p>
        {lostReasons.length === 0 ? (
          <p className="rounded-lg bg-mist p-3 text-sm text-ink/60">No lost reasons configured yet. Add some in Settings → Master Data → Lost Reasons.</p>
        ) : (
          <div className="space-y-2">
            {lostReasons.map((reason) => (
              <div key={reason} className="flex flex-wrap items-center gap-2" data-testid={`lq-row-${reason}`}>
                <span className="w-40 truncate text-sm text-ink/80">{reason}</span>
                <select className="h-9 rounded-lg border border-sky px-2 text-sm" value={lq[reason]?.type || "keep"} onChange={(e) => setLq((s) => ({ ...s, [reason]: { ...s[reason], type: e.target.value } }))}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {["set", "cap", "downgrade_if_high"].includes(lq[reason]?.type) && (
                  <select className="h-9 rounded-lg border border-sky px-2 text-sm" value={lq[reason]?.to || ""} onChange={(e) => setLq((s) => ({ ...s, [reason]: { ...s[reason], to: e.target.value } }))}>
                    <option value="">to…</option>
                    {(options?.qualities || []).map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <SaveBar saving={saving} onSave={save} />
    </div>
  );
}

/* ---------------- Rule Tester ---------------- */
function TestField({ q, value, onChange, catOptions }) {
  const t = q.question_type;
  const opts = (q.options || []).map((o) => o.value);
  if (q.category_dependent) return <select className={inp} value={value || ""} onChange={(e) => onChange(e.target.value || null)}><option value="">Select…</option>{catOptions.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (t === "Single Select") return <select className={inp} value={value || ""} onChange={(e) => onChange(e.target.value || null)}><option value="">Select…</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (t === "Yes/No") return <select className={inp} value={value || ""} onChange={(e) => onChange(e.target.value || null)}><option value="">Select…</option><option value="Yes">Yes</option><option value="No">No</option></select>;
  if (t === "Number" || t === "Currency") return <input type="number" className={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  return <input className={inp} value={value || ""} onChange={(e) => onChange(e.target.value || null)} />;
}

export function RuleTester({ flows, options }) {
  const active = flows.filter((f) => f.status === "Active");
  const [flowKey, setFlowKey] = useState(active[0]?.key || "");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const flow = flows.find((f) => f.key === flowKey);

  const segQ = (flow?.questions || []).find((q) => q.field_mapping === "business_segment");
  const catOptions = options?.business_categories?.[segQ ? answers[segQ.question_id] : null] || [];

  const run = async () => {
    if (!flowKey) return;
    setLoading(true);
    try { const { data } = await api.post("/automation/preview", { flow_key: flowKey, answers }); setResult(data.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="rule-tester">
      <div className="rounded-2xl border border-sky/70 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-loop" />
          <select data-testid="tester-flow-select" className="h-9 flex-1 rounded-lg border border-sky px-2 text-sm" value={flowKey} onChange={(e) => { setFlowKey(e.target.value); setAnswers({}); setResult(null); }}>
            {active.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
          </select>
        </div>
        <div className="space-y-2.5">
          {(flow?.questions || []).slice().sort((a, b) => a.display_order - b.display_order).map((q) => (
            <div key={q.question_id}><label className={lbl}>{q.question_label}</label>
              <TestField q={q} value={answers[q.question_id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.question_id]: v }))} catOptions={catOptions} />
            </div>
          ))}
        </div>
        <button onClick={run} disabled={loading} data-testid="tester-run-button" className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run test"}</button>
      </div>
      <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid="tester-result">
        <h3 className="mb-3 font-sora text-sm font-semibold text-navy">Result</h3>
        {!result ? <p className="py-8 text-center text-sm text-ink/40">Fill the answers and run a test to preview the computed quality.</p> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <QualityChip value={result.quality} />
              <span className="font-sora text-lg font-bold text-navy">Score {result.score}</span>
              {result.status !== "Qualified" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{result.status}</span>}
            </div>
            {result.base_quality !== result.quality && <p className="text-xs text-ink/50">Base score quality <b>{result.base_quality}</b> → adjusted to <b className="text-navy">{result.quality}</b>.</p>}
            {result.reasons?.length > 0 && (
              <div><p className="mb-1 flex items-center gap-1 text-xs font-semibold text-ink/60"><Info className="h-3.5 w-3.5" />Why</p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-ink/60">{result.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
            )}
            {result.breakdown?.length > 0 && (
              <div className="rounded-lg bg-mist/40 p-2">
                <p className="mb-1 text-[11px] font-semibold text-ink/60">Score breakdown</p>
                {result.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-[11px] text-ink/60"><span className="truncate pr-2">{b.question_label}: {String(b.answer)}</span><span className={`font-semibold ${b.score < 0 ? "text-red-500" : "text-emerald-600"}`}>{b.score > 0 ? "+" : ""}{b.score}</span></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
